import React, { useState } from "react";
import { getCurrencySymbol } from "../../../helper/formatCurrency";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import {
  ProductAvailabilityHeader,
  AvailabilityTable,
  MobileCardView,
} from "./components";
import { useProductAvailability } from "./hooks";
import {
  exportAvailability,
  exportAvailabilityAsCSV,
  paginateData,
} from "./utils";
import { MOBILE_BREAKPOINT } from "./utils/constants";

/**
 * Product Availability Page
 * Displays product availability status with export capability
 * Supports both mobile card view and desktop table view
 */
function ProductAvailability() {
  const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    flattenedData,
    searchQuery,
    page,
    rowsPerPage,
    loading,
    handleSearch,
    setPage,
  } = useProductAvailability();

  const { locale, restaurantName } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  // Paginate data for both mobile and desktop views
  const displayData = paginateData(flattenedData, page, rowsPerPage);

  // Calculate total pages
  const totalPages = Math.ceil(flattenedData.length / rowsPerPage);

  // Export handlers
  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExportPDF = () => {
    exportAvailability(flattenedData, restaurantName, currencySymbol);
  };

  const handleExportCSV = () => {
    exportAvailabilityAsCSV(flattenedData);
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 text-sm">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3 overflow-hidden">
      {/* Header Section */}
      <div className="flex-shrink-0">
        <ProductAvailabilityHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          onExportClick={handleExportClick}
          onExportClose={handleExportClose}
          onExportPDF={handleExportPDF}
          onExportCSV={handleExportCSV}
          anchorEl={anchorEl}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-hidden">
        {isMobile ? (
          <div className="h-full overflow-auto">
            <MobileCardView
              products={displayData}
              currencySymbol={currencySymbol}
            />

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 py-4 px-4">
                <div className="flex justify-center items-center gap-4">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <AvailabilityTable
            products={displayData}
            currencySymbol={currencySymbol}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}

export default ProductAvailability;
